import React, { createRef } from "react";
import {
    Container,
    Breadcrumb,
    Button,
    Table,
    Modal,
    Form,
    Row,
    Col,
    Accordion,
} from "react-bootstrap";
import { Link, withRouter } from "react-router-dom";
import "currency-flags/dist/currency-flags.min.css";
import {
    faCheck,
    faDownload,
    faEdit,
    faPlus,
    faTimes,
    faTrash,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import MyTable from "components/MyTable";
import ProcessingButton from "components/ProcessingButton";
import CrawlingSchedulerService from "services/CrawlingSchedulerService";
import Toast from "services/Toast";
import OrganizationSearch from "components/OrganizationSearch";
import WebsiteSearch from "components/WebsiteSearch";
import ProductSearch from "components/ProductSearch";
import BrandSearch from "components/BrandSearch";
import confirm from "components/Confirm";

/**
 * Crawling Scheduler component
 * @component
 * @category Others
 */
class CrawlingScheduler extends React.Component {
    constructor(props) {
        super(props);
        this.state = { edit: null, loading: true };
        this.tableRef = createRef();
        this.productRef = createRef();
        this.websiteRef = createRef();
        this.brandRef = createRef();

        this.onHide = this.onHide.bind(this);
        this.onAdd = this.onAdd.bind(this);
        this.onEdit = this.onEdit.bind(this);
        this.onDelete = this.onDelete.bind(this);
        this.onSaveChanges = this.onSaveChanges.bind(this);
        this.onChangeFrequency = this.onChangeFrequency.bind(this);
        this.handleSwitchStatus = this.handleSwitchStatus.bind(this);
        this.handleSwitchDynamicCrawling =
            this.handleSwitchDynamicCrawling.bind(this);
        this.downloadSpreadsheet = this.downloadSpreadsheet.bind(this);
        this.handleAddElement = this.handleAddElement.bind(this);
        this.handleAddBrand = this.handleAddBrand.bind(this);
        this.removeElement = this.removeElement.bind(this);
        this.handleSelectOrganization =
            this.handleSelectOrganization.bind(this);
        this.onAccordionClick = this.onAccordionClick.bind(this);
    }

    componentDidMount() {
        CrawlingSchedulerService.rules().then((result) => {
            if (result === false) {
                Toast.error(
                    "Something went wrong while retrieving crawling scheduling"
                );
                return;
            }
            let list = result.filter((row) => row.organization !== null);
            this.tableRef.current.populate(list);
            this.setState({ loading: false });
        });
    }

    onAccordionClick(evt) {
        let key = evt.target.parentNode.parentNode.getAttribute("accordionKey");
        this.setState({ accordion: key });
    }

    downloadSpreadsheet() {
        this.tableRef.current.CSV();
    }

    onHide(evt) {
        if (this.state.saving) {
            return;
        }
        this.setState({ edit: null });
    }

    onChangeFrequency(evt) {
        const day = evt.target.getAttribute("day");
        let edit = this.state.edit;
        edit[day] = evt.target.value;
        this.setState({ edit });
    }

    async onDelete(evt) {
        let obj = this.tableRef.current.getRowFromEvent(evt);
        if (
            !(await confirm("Are you sure?", "Continue", "Cancel", {
                message:
                    "You're about to remove one set of settings for \"" +
                    obj.row.organization.name +
                    "\". This operation can't be undone. Do you want to continue?",
            }))
        ) {
            return;
        }

        this.tableRef.current.setLoading(true);
        CrawlingSchedulerService.deleteRule(obj.row.id).then((result) => {
            this.tableRef.current.setLoading(false);
            if (!result) {
                Toast.error("Something went wrong while removing this rule.");
            } else {
                this.tableRef.current.deleteRow(obj.row);
                Toast.ok("Rule removed successfully");
            }
        });
    }

    onAdd() {
        this.setState({
            accordion: 0,
            edit: {
                mode: "add",
                id: 0,
                is_active: true,
                sunday: 0,
                monday: 0,
                tuesday: 0,
                wednesday: 0,
                thursday: 0,
                friday: 0,
                saturday: 0,
                brands: [],
                selectedBrands: [],
                products: [],
                websites: [],
                organization: null,
                dynamic_crawling: false,
            },
        });
    }

    handleSwitchStatus(evt) {
        const checked = evt.target.checked;
        const edit = this.state.edit;
        edit.is_active = checked;
        this.setState({ edit });
    }

    handleSwitchDynamicCrawling(evt) {
        const checked = evt.target.checked;
        const edit = this.state.edit;
        edit.dynamic_crawling = checked;
        this.setState({ edit });
    }

    onEdit(evt) {
        const obj = this.tableRef.current.getRowFromEvent(evt);
        const row = JSON.parse(JSON.stringify(obj.row));
        const sanitizeValue = (key) => {
            return typeof row.weekdays[key] === "number"
                ? row.weekdays[key]
                : 0;
        };

        this.setState(
            {
                accordion: 2,
                edit: {
                    mode: "edit",
                    id: row.id,
                    is_active: row.is_active,
                    sunday: sanitizeValue("sunday"),
                    monday: sanitizeValue("monday"),
                    tuesday: sanitizeValue("tuesday"),
                    wednesday: sanitizeValue("wednesday"),
                    thursday: sanitizeValue("thursday"),
                    friday: sanitizeValue("friday"),
                    saturday: sanitizeValue("saturday"),
                    selectedBrands: row.brands,
                    brands: row.brands.map((brand) => brand.id),
                    products: row.products,
                    websites: row.websites,
                    organization: row.organization,
                    dynamic_crawling: row.dynamic_crawling,
                },
            },
            () => {
                this.row = row;
            }
        );
    }

    handleAddElement(element) {
        const edit = this.state.edit,
            type =
                typeof element[0].upc === "undefined" ? "websites" : "products",
            componentRef =
                type === "websites" ? this.websiteRef : this.productRef;
        edit[type].push(element[0]);
        this.setState({ edit });
        componentRef.current?.clear();
    }

    handleAddBrand(brands) {
        const edit = this.state.edit;
        edit.selectedBrands.push(brands[0]);
        this.setState({ edit });
        this.brandRef.current?.clear();
    }

    handleSelectOrganization(organization) {
        const edit = this.state.edit;
        const hasOrganization = organization.length > 0;

        edit.organization = hasOrganization ? organization[0] : null;
        edit.brands = hasOrganization
            ? edit.organization.brands
                  .filter((brand) => brand.active)
                  .map((brand) => parseInt(brand.id))
            : [];
        edit.selectedBrands = hasOrganization
            ? edit.organization.brands.filter((brand) => brand.active)
            : [];
        edit.products = [];
        this.setState({ edit, accordion: 1 });
    }

    removeElement(evt) {
        const target = /path/i.test(evt.target.nodeName)
                ? evt.target.parentNode
                : evt.target,
            type =
                target.getAttribute("wid") !== null
                    ? "websites"
                    : target.getAttribute("bid") !== null
                    ? "selectedBrands"
                    : "products",
            index = target.getAttribute("pindex"),
            edit = this.state.edit;

        edit[type].splice(index, 1);
        this.setState({ edit });
    }

    onSaveChanges(evt) {
        const dynamicCrawling = this.state.edit.dynamic_crawling;
        const editMode = this.state.edit.mode === "edit",
            payload = editMode
                ? {
                      description: this.row.description,
                      is_generic: this.row.is_generic,
                      is_active: this.state.edit.is_active,
                      organization_id: this.row.organization.id,
                      weekdays: {},
                      dynamic_crawling: dynamicCrawling,
                  }
                : {
                      description: this.state.edit.organization.name,
                      is_generic: true,
                      is_active: this.state.edit.is_active,
                      organization_id: this.state.edit.organization.id,
                      weekdays: {},
                      dynamic_crawling: dynamicCrawling,
                  };
        [
            "sunday",
            "monday",
            "tuesday",
            "wednesday",
            "thursday",
            "friday",
            "saturday",
        ].forEach((day) => {
            payload.weekdays[day] = dynamicCrawling
                ? 0
                : parseInt(this.state.edit[day]);
        });
        payload.product_ids = this.state.edit.products.map((row) =>
            parseInt(row.id)
        );
        payload.website_ids = this.state.edit.websites.map((row) =>
            parseInt(row.id)
        );
        if (dynamicCrawling) {
            const brands = this.state.edit.selectedBrands.filter(
                (brand) => brand.active ?? true
            );
            payload.brand_ids = brands.map((brand) => parseInt(brand.id));
            payload.product_ids = [];
        }
        this.setState({ saving: true });

        if (editMode) {
            CrawlingSchedulerService.updateRule(this.row.id, payload).then(
                (row) => {
                    if (row === false) {
                        Toast.error(
                            "Something went wrong while saving changes."
                        );
                        this.setState({ saving: false });
                        return;
                    }
                    Object.keys(row).forEach((field) => {
                        this.row[field] = row[field];
                    });
                    this.tableRef.current.updateRow(this.row);
                    this.setState({ edit: null, saving: false });
                    delete this.row;
                }
            );
        } else {
            CrawlingSchedulerService.addRule(payload).then((result) => {
                if (!result || result._error !== undefined) {
                    Toast.error(
                        result?._error ||
                            "Something went wrong while adding new rule."
                    );
                    this.setState({ saving: false });
                    return;
                }
                this.tableRef.current.addRow(result);
                this.setState({ edit: null, saving: false });
            });
        }
    }

    FormModal = ({ edit }) => {
        if (edit === null) return null;

        const isEdit = edit.mode === "edit";
        const hasOrganization = edit.organization !== null;
        const isDynamicCrawling = edit.dynamic_crawling;
        const DCOnly = {
            display: isDynamicCrawling ? "block" : "none",
        };
        const NotDC = {
            display: isDynamicCrawling ? "none" : "block",
        };

        const DayInput = ({ attribute }) => {
            return (
                <Form.Control
                    onChange={this.onChangeFrequency}
                    value={edit[attribute]}
                    type="number"
                    min={0}
                    max={24}
                    day={attribute}
                />
            );
        };

        return (
            <Modal
                size="lg"
                show={true}
                backdrop={true}
                keyboard={true}
                centered
            >
                <Modal.Header closeButton onHide={this.onHide}>
                    <Modal.Title>
                        {isEdit ? "Edit schedule" : "New schedule"}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body className="text-center">
                    <Accordion
                        defaultActiveKey={`${this.state.accordion}`}
                        activeKey={`${this.state.accordion}`}
                    >
                        {!isEdit && (
                            <Accordion.Item accordionKey="0" eventKey="0">
                                <Accordion.Header
                                    onClick={this.onAccordionClick}
                                >
                                    Organization
                                </Accordion.Header>
                                <Accordion.Body>
                                    <OrganizationSearch
                                        onChange={this.handleSelectOrganization}
                                        placeholder="Select an organization"
                                    />
                                </Accordion.Body>
                            </Accordion.Item>
                        )}
                        <Accordion.Item accordionKey="1" eventKey="1">
                            <Accordion.Header onClick={this.onAccordionClick}>
                                Settings
                            </Accordion.Header>
                            <Accordion.Body>
                                <Row>
                                    <Col
                                        sm={2}
                                        className="d-flex align-items-center justify-content-start"
                                    >
                                        <Form.Check
                                            type="switch"
                                            label={
                                                edit.is_active
                                                    ? "Active"
                                                    : "Disabled"
                                            }
                                            checked={edit.is_active}
                                            onChange={this.handleSwitchStatus}
                                        />
                                    </Col>
                                    <Col
                                        sm={3}
                                        className="d-flex align-items-center justify-content-end"
                                    >
                                        Dynamic Crawling
                                    </Col>
                                    <Col sm={2}>
                                        <Form.Check
                                            type="switch"
                                            label={
                                                edit.dynamic_crawling
                                                    ? "On"
                                                    : "Off"
                                            }
                                            checked={edit.dynamic_crawling}
                                            onChange={
                                                this.handleSwitchDynamicCrawling
                                            }
                                        />
                                    </Col>
                                </Row>
                            </Accordion.Body>
                        </Accordion.Item>
                        <Accordion.Item
                            accordionKey="2"
                            eventKey="2"
                            style={NotDC}
                        >
                            <Accordion.Header onClick={this.onAccordionClick}>
                                Calendar
                            </Accordion.Header>
                            <Accordion.Body>
                                <Table
                                    style={{
                                        marginBottom: "0px",
                                    }}
                                >
                                    <thead>
                                        <tr>
                                            <th>Sunday</th>
                                            <th>Monday</th>
                                            <th>Tuesday</th>
                                            <th>Wednesday</th>
                                            <th>Thursday</th>
                                            <th>Friday</th>
                                            <th>Saturday</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr>
                                            <td>
                                                <DayInput attribute="sunday" />
                                            </td>
                                            <td>
                                                <DayInput attribute="monday" />
                                            </td>
                                            <td>
                                                <DayInput attribute="tuesday" />
                                            </td>
                                            <td>
                                                <DayInput attribute="wednesday" />
                                            </td>
                                            <td>
                                                <DayInput attribute="thursday" />
                                            </td>
                                            <td>
                                                <DayInput attribute="friday" />
                                            </td>
                                            <td>
                                                <DayInput attribute="saturday" />
                                            </td>
                                        </tr>
                                    </tbody>
                                </Table>
                            </Accordion.Body>
                        </Accordion.Item>
                        <Accordion.Item
                            accordionKey="3"
                            eventKey="3"
                            style={DCOnly}
                        >
                            <Accordion.Header onClick={this.onAccordionClick}>
                                Brands
                            </Accordion.Header>
                            <Accordion.Body>
                                <BrandSearch
                                    ref={this.brandRef}
                                    onChange={this.handleAddBrand}
                                    organization={edit.organization?.id}
                                    placeholder="Select a brand"
                                    skip={edit.selectedBrands
                                        .filter((brand) => brand.active)
                                        .map((brand) => parseInt(brand.id))}
                                />
                                <Container>
                                    <Row>
                                        {edit.selectedBrands.map(
                                            (brand, index) => (
                                                <Col
                                                    sm={4}
                                                    key={`brand-${brand.id}`}
                                                    className="text-left"
                                                >
                                                    <FontAwesomeIcon
                                                        icon={faTimes}
                                                        className="pointer"
                                                        onClick={
                                                            this.removeElement
                                                        }
                                                        bid={brand.id}
                                                        pindex={index}
                                                    />{" "}
                                                    {brand.name}
                                                </Col>
                                            )
                                        )}
                                    </Row>
                                </Container>
                            </Accordion.Body>
                        </Accordion.Item>
                        <Accordion.Item accordionKey="4" eventKey="4">
                            <Accordion.Header onClick={this.onAccordionClick}>
                                Websites
                            </Accordion.Header>
                            <Accordion.Body>
                                <WebsiteSearch
                                    ref={this.websiteRef}
                                    onChange={this.handleAddElement}
                                    placeholder="Select a website"
                                    skip={edit.websites.map((row) => row.id)}
                                />
                                <Container>
                                    <Row>
                                        {edit.websites.map((website, index) => (
                                            <Col
                                                sm={4}
                                                key={`website-${website.id}`}
                                                className="text-left"
                                            >
                                                <FontAwesomeIcon
                                                    icon={faTimes}
                                                    className="pointer"
                                                    onClick={this.removeElement}
                                                    wid={website.id}
                                                    pindex={index}
                                                />{" "}
                                                {website.display_name ??
                                                    website.name}
                                            </Col>
                                        ))}
                                    </Row>
                                </Container>
                            </Accordion.Body>
                        </Accordion.Item>{" "}
                        <Accordion.Item
                            accordionKey="5"
                            eventKey="5"
                            style={NotDC}
                        >
                            <Accordion.Header onClick={this.onAccordionClick}>
                                Products
                            </Accordion.Header>
                            <Accordion.Body>
                                <ProductSearch
                                    ref={this.productRef}
                                    onChange={this.handleAddElement}
                                    includeArchivedProducts={false}
                                    includeDisabledBrands={false}
                                    placeholder="Add product"
                                    brands={edit.brands}
                                    skip={edit.products.map((row) => row.id)}
                                />
                                <Container>
                                    <Row>
                                        {edit.products.map((product, index) => (
                                            <Col
                                                sm={3}
                                                key={`product-${product.id}`}
                                            >
                                                <FontAwesomeIcon
                                                    className="pointer"
                                                    icon={faTimes}
                                                    pid={product.id}
                                                    pindex={index}
                                                    onClick={this.removeElement}
                                                />{" "}
                                                {product.upc}
                                            </Col>
                                        ))}
                                    </Row>
                                </Container>
                            </Accordion.Body>
                        </Accordion.Item>
                    </Accordion>
                </Modal.Body>
                <Modal.Footer
                    style={{
                        justifyContent: "center",
                    }}
                >
                    <Button size="sm" variant="secondary" onClick={this.onHide}>
                        Cancel
                    </Button>
                    <ProcessingButton
                        disabled={
                            !hasOrganization ||
                            (isDynamicCrawling &&
                                edit.selectedBrands.length === 0)
                        }
                        size="sm"
                        processing={this.state.saving}
                        processingLabel="Saving ..."
                        label="Save"
                        onClick={this.onSaveChanges}
                    />
                </Modal.Footer>
            </Modal>
        );
    };

    render() {
        return (
            <Container className="pt-4">
                <span
                    style={{
                        float: "right",
                    }}
                >
                    <Button
                        size="sm"
                        className="tdButton"
                        onClick={this.downloadSpreadsheet}
                        disabled={this.state.loading}
                        variant="success"
                    >
                        <FontAwesomeIcon
                            icon={faDownload}
                            style={{
                                marginRight: "5px",
                            }}
                        />
                        Download
                    </Button>
                </span>

                <Breadcrumb className="dd">
                    <Breadcrumb.Item>Others</Breadcrumb.Item>
                    <Breadcrumb.Item active>Crawling Scheduler</Breadcrumb.Item>
                </Breadcrumb>
                <this.FormModal edit={this.state.edit} />
                <MyTable
                    name="crawling-schedule"
                    dynamic={false}
                    ref={this.tableRef}
                    sortBy="organization"
                    sortOrder="asc"
                    headers={[
                        {
                            field: "organization",
                            label: "Organization",
                            sortable: true,
                            searchable: true,
                        },
                        {
                            field: "brands",
                            label: "Brands",
                            sortable: true,
                            searchable: true,
                        },
                        {
                            field: "websites",
                            label: "Websites",
                            sortable: true,
                            searchable: true,
                        },
                        {
                            field: "products",
                            label: "Products",
                            width: "400px",
                            sortable: true,
                            searchable: true,
                        },
                        {
                            field: "calendar",
                            label: "Schedule",
                            sortable: true,
                        },
                        {
                            field: "dynamic_crawling",
                            label: "DC Mode",
                            hint: "Dynamic Crawling Mode",
                            width: "80px",
                            sortable: true,
                            allowEmpty: true,
                            options: ["On", "Off"],
                        },
                        {
                            field: "is_active",
                            label: "Status",
                            width: "70px",
                            sortable: true,
                            allowEmpty: true,
                            options: [
                                { label: "Active", value: "1" },
                                { label: "Disabled", value: "0" },
                            ],
                        },
                        {
                            field: "control",
                            width: "87px",
                            content: (
                                <Button
                                    onClick={this.onAdd}
                                    size="sm"
                                    className="tdButton"
                                    variant="success"
                                >
                                    <FontAwesomeIcon icon={faPlus} />
                                </Button>
                            ),
                        },
                    ]}
                    renderColumns={{
                        organization: {
                            format: (row) => (
                                <Link
                                    className="link"
                                    to={`/organization/${row.organization.id}`}
                                >
                                    (#{row.organization.id}){" "}
                                    {row.organization.name}
                                </Link>
                            ),
                            text: (row) => row.organization.name,
                            csv: (row) =>
                                "(#" +
                                row.organization.id +
                                ") " +
                                row.organization.name,
                        },
                        brands: {
                            format: (row) =>
                                row.brands.map((r) => (
                                    <div key={`r${row.id}-brand-${r.id}`}>
                                        <Link
                                            className="link"
                                            to={`/brand/${r.id}#restrictions`}
                                        >
                                            (#{r.id}) {r.name}
                                        </Link>
                                    </div>
                                )),
                            text: (row) =>
                                row.brands
                                    .map((r) => r.id + " " + r.name)
                                    .join(";"),
                            csv: (row) =>
                                row.brands
                                    .map((r) => "(#" + r.id + ") " + r.name)
                                    .join("; "),
                        },
                        websites: {
                            format: (row) =>
                                Array.isArray(row.websites)
                                    ? row.websites.map((row) => (
                                          <Link
                                              key={`r${row.id}-website-${row.id}`}
                                              className="plink"
                                              to={`/website/${row.id}`}
                                          >
                                              {row.name}
                                          </Link>
                                      ))
                                    : null,
                            text: (row) =>
                                Array.isArray(row.websites)
                                    ? row.websites
                                          .map((row) => row.name)
                                          .join(", ")
                                    : "",
                            csv: (row) =>
                                Array.isArray(row.websites)
                                    ? row.websites
                                          .map((row) => row.name)
                                          .join(", ")
                                    : "",
                        },
                        dynamic_crawling: {
                            className: "text-center",
                            format: (row) =>
                                row.dynamic_crawling ? (
                                    <FontAwesomeIcon
                                        icon={faCheck}
                                        style={{ color: "green" }}
                                    />
                                ) : (
                                    <FontAwesomeIcon
                                        icon={faTimes}
                                        style={{ color: "red" }}
                                    />
                                ),
                            text: (row) =>
                                row.dynamic_crawling ? "On" : "Off",
                        },
                        products: {
                            format: (row) =>
                                Array.isArray(row.products)
                                    ? row.products.map((row) => (
                                          <Link
                                              key={`r${row.id}-p${row.id}`}
                                              className="plink"
                                              to={`/product/${row.id}`}
                                          >
                                              {row.upc}
                                          </Link>
                                      ))
                                    : null,
                            text: (row) =>
                                Array.isArray(row.products)
                                    ? row.products
                                          .map((row) => row.upc)
                                          .join(", ")
                                    : "",
                            csv: (row) =>
                                Array.isArray(row.products)
                                    ? row.products
                                          .map((row) => row.upc)
                                          .join(", ")
                                    : "",
                        },
                        is_active: {
                            className: "text-center",
                            format: (row) =>
                                row.is_active ? (
                                    <FontAwesomeIcon
                                        icon={faCheck}
                                        style={{ color: "green" }}
                                    />
                                ) : (
                                    <FontAwesomeIcon
                                        icon={faTimes}
                                        style={{ color: "red" }}
                                    />
                                ),
                            text: (row) => (row.is_active ? "1" : "0"),
                            csv: (row) =>
                                row.is_active ? "Active" : "Disabled",
                        },
                        calendar: {
                            format: (row) => {
                                if (row.dynamic_crawling) return "N/A";

                                const sanitize = (key) => {
                                    return typeof row.weekdays[key] ===
                                        "number" && row.weekdays[key]
                                        ? row.weekdays[key]
                                        : "";
                                };
                                const valueStyle = {
                                    borderBottomWidth: "0px",
                                    textAlign: "center",
                                };
                                return (
                                    <Table
                                        style={{
                                            marginBottom: "0px",
                                        }}
                                        key={`calendar-org${row.id}`}
                                    >
                                        <thead>
                                            <tr>
                                                <th className="text-center">
                                                    Sun
                                                </th>
                                                <th className="text-center">
                                                    Mon
                                                </th>
                                                <th className="text-center">
                                                    Tue
                                                </th>
                                                <th className="text-center">
                                                    Wed
                                                </th>
                                                <th className="text-center">
                                                    Thu
                                                </th>
                                                <th className="text-center">
                                                    Fri
                                                </th>
                                                <th className="text-center">
                                                    Sat
                                                </th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            <tr>
                                                <td style={valueStyle}>
                                                    {sanitize("sunday")}
                                                </td>
                                                <td style={valueStyle}>
                                                    {sanitize("monday")}
                                                </td>
                                                <td style={valueStyle}>
                                                    {sanitize("tuesday")}
                                                </td>
                                                <td style={valueStyle}>
                                                    {sanitize("wednesday")}
                                                </td>
                                                <td style={valueStyle}>
                                                    {sanitize("thursday")}
                                                </td>
                                                <td style={valueStyle}>
                                                    {sanitize("friday")}
                                                </td>
                                                <td style={valueStyle}>
                                                    {sanitize("saturday")}
                                                </td>
                                            </tr>
                                        </tbody>
                                    </Table>
                                );
                            },
                            text: (row) =>
                                parseInt(row["sunday"]) +
                                parseInt(row["monday"]) +
                                parseInt(row["tuesday"]) +
                                parseInt(row["wednesday"]) +
                                parseInt(row["thursday"]) +
                                parseInt(row["friday"]) +
                                parseInt(row["saturday"]) +
                                "",
                            csv: (row) =>
                                JSON.stringify({
                                    sunday: row["sunday"],
                                    monday: row["monday"],
                                    tuesday: row["tuesday"],
                                    wednesday: row["wednesday"],
                                    thursday: row["thursday"],
                                    friday: row["friday"],
                                    saturday: row["saturday"],
                                }),
                        },
                        control: {
                            className: "text-center",
                            format: (row) => (
                                <React.Fragment>
                                    <Button
                                        onClick={this.onEdit}
                                        size="sm"
                                        className="tdButton"
                                        variant="secondary"
                                    >
                                        <FontAwesomeIcon icon={faEdit} />
                                    </Button>
                                    <Button
                                        onClick={this.onDelete}
                                        size="sm"
                                        className="tdButton"
                                        variant="danger"
                                    >
                                        <FontAwesomeIcon icon={faTrash} />
                                    </Button>
                                </React.Fragment>
                            ),
                        },
                    }}
                />
            </Container>
        );
    }
}

export default withRouter(CrawlingScheduler);
